import React, { useState, useEffect, useCallback } from 'react'
import { Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { amazonAPI } from '../api/amazonAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import {
    formatDateTime, utcTimeToLocal, localTimeToUtc, HOUR_OPTIONS, MINUTE_OPTIONS,
} from '../utils/commonUtils'
import { AmazonSettingsSectionHeader } from './AmazonScopeChip'

const DATA_COLLECTION_PATH = '/amazon-data-collection-settings'
const NOT_SCHEDULED = {
    runs_off: 'Not scheduled: daily runs are off for this account.',
    collection_off: 'Not scheduled: data collection is off for this account, so there is no fresh data to judge.',
    account_inactive: 'Not scheduled: this Amazon account is inactive.',
    invalid_time: 'Not scheduled: the stored run time is invalid; choose a time and save.',
}

const formFromConfig = (config) => {
    const local = utcTimeToLocal(config.automations_hour, config.automations_minute)
    return { enabled: !!config.automations_enabled, hour: local.hour, minute: local.minute }
}

/**
 * General tab of Ad Automation Settings: when the daily run of all automations happens for one
 * account. Stored on the account's data collection config (UTC), edited in local time.
 */
function AmazonAutomationSchedulePanel({ accountId, canManage, onDirtyChange }) {
    const [config, setConfig] = useState(null)
    const [form, setForm] = useState(null)
    const [savedForm, setSavedForm] = useState(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [saved, setSaved] = useState(false)

    const dirty = !!form && !!savedForm && JSON.stringify(form) !== JSON.stringify(savedForm)
    useEffect(() => { onDirtyChange?.(dirty) }, [dirty, onDirtyChange])

    const apply = (data) => {
        const built = formFromConfig(data)
        setConfig(data)
        setForm(built)
        setSavedForm(built)
    }

    const load = useCallback(async () => {
        if (!accountId) return
        setLoading(true)
        setError('')
        setSaved(false)
        try {
            apply(await amazonAPI.collectionConfig.get(accountId))
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setLoading(false)
        }
    }, [accountId])

    useEffect(() => { load() }, [load])

    const update = (patch) => { setForm(f => ({ ...f, ...patch })); setSaved(false); setError('') }

    const handleSave = async (e) => {
        e.preventDefault()
        setSaving(true)
        setError('')
        try {
            const utc = localTimeToUtc(Number(form.hour), Number(form.minute))
            // Only the automation fields: the rest of the config belongs to Data Collection Settings
            apply(await amazonAPI.collectionConfig.update(accountId, {
                automations_enabled: form.enabled,
                automations_hour: utc.hour,
                automations_minute: utc.minute,
            }))
            setSaved(true)
        } catch (err) {
            setError(err.response?.data?.error || formatErrorResponse(err))
        } finally {
            setSaving(false)
        }
    }

    if (loading) return <div className="text-center py-5"><Spinner animation="border" /></div>
    if (!config || !form) return error ? <Alert variant="danger">{error}</Alert> : null

    const next = config.next_automations_run || {}
    const collectionOff = !config.is_enabled
    let nextLine
    if (dirty) {
        nextLine = 'Save to see the next run with these settings.'
    } else if (next.not_scheduled_reason) {
        nextLine = NOT_SCHEDULED[next.not_scheduled_reason] || 'Not scheduled.'
    } else if (next.due_now) {
        nextLine = 'Next run: due now, starts within 15 minutes.'
    } else {
        nextLine = `Next run: ${formatDateTime(next.at)}`
    }

    return (
        <Form onSubmit={handleSave} style={{ maxWidth: '730px' }}>
            <Card className="mb-4">
                <Card.Body>
                    <AmazonSettingsSectionHeader
                        title="Daily run"
                        scope="all"
                        scopeLabel="All automations · this account"
                        sentence="When Inventory-Aware Bidding and Anomaly Detection run every day for this account. Run on the Ad Automation page works at any time, regardless of this."
                    />
                    <Form.Check
                        type="switch"
                        id="automations-enabled"
                        className="mb-3"
                        label="Run all automations daily for this account"
                        checked={form.enabled}
                        disabled={!canManage}
                        onChange={e => update({ enabled: e.target.checked })}
                    />
                    <Form.Group className="mb-3">
                        <Form.Label>Run time (your local time)</Form.Label>
                        <Row className="g-2" style={{ maxWidth: '320px' }}>
                            <Col xs={6}>
                                <Form.Select
                                    value={form.hour}
                                    disabled={!canManage || !form.enabled}
                                    onChange={e => update({ hour: Number(e.target.value) })}
                                >
                                    {HOUR_OPTIONS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                                </Form.Select>
                            </Col>
                            <Col xs={6}>
                                <Form.Select
                                    value={form.minute}
                                    disabled={!canManage || !form.enabled}
                                    onChange={e => update({ minute: Number(e.target.value) })}
                                >
                                    {MINUTE_OPTIONS.map(m => <option key={m.value} value={m.value}>:{m.label}</option>)}
                                </Form.Select>
                            </Col>
                        </Row>
                        <Form.Text>
                            Pick a time a few hours after the nightly data collection, so the day&apos;s reports are in.
                            The run starts within 15 minutes of this time.
                        </Form.Text>
                    </Form.Group>

                    <div className={`small ${next.not_scheduled_reason && !dirty ? 'text-warning-emphasis' : ''}`}>
                        {nextLine}
                        {!dirty && collectionOff && (
                            <> <Link to={DATA_COLLECTION_PATH}>Open Data Collection Settings</Link></>
                        )}
                    </div>
                    <div className="small text-body-secondary mt-1">
                        Last run: {config.last_automations_run_at ? formatDateTime(config.last_automations_run_at) : ''}
                    </div>
                </Card.Body>
            </Card>

            {canManage && (
                <div className="mb-4">
                    {error && <Alert variant="danger" className="mb-2">{error}</Alert>}
                    {saved && <Alert variant="success" className="mb-2">Daily run saved.</Alert>}
                    <Button type="submit" variant="primary" disabled={saving || !dirty}>
                        {saving ? <Spinner size="sm" /> : 'Save'}
                    </Button>
                </div>
            )}
        </Form>
    )
}

export default AmazonAutomationSchedulePanel
